import { useMemo } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useApi } from '../api';
import { readCache, writeCache } from './localStorage';
import type { ModuleItem } from './types';

export const MODULE_QUERY_KEY = ['ref', 'modules'] as const;

const CACHE_KEY = 'modules';
const STALE_TIME = 30 * 60 * 1000; // 30 min
const GC_TIME = 60 * 60 * 1000; // 1 hour

export interface UseModulesResult {
  query: UseQueryResult<ModuleItem[]>;
  modules: ModuleItem[];
  moduleNameMap: Map<string, string>;
  getModulesForSubject: (subject: string) => ModuleItem[];
}

export function useModules(): UseModulesResult {
  const { fetchApi } = useApi();

  const cached = useMemo(() => readCache<ModuleItem[]>(CACHE_KEY), []);

  const query = useQuery({
    queryKey: [...MODULE_QUERY_KEY],
    queryFn: async () => {
      const res = await fetchApi<{ modules: ModuleItem[] }>('/modules');
      writeCache(CACHE_KEY, res.modules);
      return res.modules;
    },
    initialData: cached?.data,
    initialDataUpdatedAt: cached?.updatedAt,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });

  const modules = query.data ?? [];

  const moduleNameMap = useMemo(
    () => new Map(modules.map(m => [m.id, m.displayName])),
    [modules],
  );

  const getModulesForSubject = useMemo(
    () => (subject: string) => modules.filter(m => m.subject === subject),
    [modules],
  );

  return { query, modules, moduleNameMap, getModulesForSubject };
}
